import React, { useState, useEffect } from 'react';
import { Search, MapPin, TrendingDown, ShoppingCart, Loader2, Clock, Zap, ExternalLink, Tag, Heart } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast, Toaster } from 'sonner';
import axios from 'axios';
import { API } from '../App';

const TRENDING = [
  'Amul Milk', 'Aashirvaad Atta 5kg', 'Maggi Noodles', 'Coca Cola',
  'Lays Chips', 'Britannia Bread', 'Surf Excel', 'Dove Soap',
  'Onions 1kg', 'Eggs 6 pcs', 'Bananas 1 dozen', 'Tata Salt',
];

const GroceryPriceCompare = () => {
  const [query, setQuery] = useState('');
  const [location, setLocation] = useState(null);
  const [locationName, setLocationName] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => { detectLocation(); }, []);

  const detectLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const { latitude: lat, longitude: lng } = pos.coords;
          setLocation({ lat, lng });
          try {
            const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`);
            const data = await res.json();
            setLocationName(data.address?.suburb || data.address?.city_district || data.address?.city || `${lat.toFixed(2)}, ${lng.toFixed(2)}`);
          } catch {
            setLocationName(`${lat.toFixed(2)}, ${lng.toFixed(2)}`);
          }
        },
        () => setLocationName('Bangalore (default)'),
        { timeout: 8000 }
      );
    } else {
      setLocationName('Bangalore (default)');
    }
  };

  const search = async (q) => {
    const productName = (q || query).trim();
    if (!productName) {
      toast.error('Type a product to compare');
      return;
    }
    setLoading(true);
    setResults(null);
    try {
      const body = {
        product_name: productName,
        lat: location?.lat,
        lng: location?.lng,
      };
      const r = await axios.post(`${API}/grocery-compare/search`, body);
      setResults(r.data);
      if ((r.data.products || []).length === 0) {
        toast.error('No price estimates found. Try a different product.');
      } else {
        toast.success(`Found ${r.data.products.length} variants!`);
      }
    } catch (err) {
      toast.error('Search failed. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const bd = results?.best_deal;

  return (
    <div className="min-h-screen bg-[#09090b] text-white" data-testid="grocery-compare-page">
      <Toaster position="top-right" richColors theme="dark" />

      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#09090b]/80 border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a href="/" className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center hover:scale-110 transition-transform" data-testid="back-to-home"><Heart className="w-4 h-4 text-white" /></a>
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center"><ShoppingCart className="w-4 h-4 text-black" /></div>
            <div>
              <h1 className="text-base font-bold tracking-tight">GroceryCompare AI</h1>
              <p className="text-[10px] text-zinc-500">Blinkit vs Zepto. 10-min delivery. Best price.</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/food-compare" className="text-xs text-zinc-500 hover:text-white transition hidden sm:block">Food</a>
            <a href="/ride-compare" className="text-xs text-zinc-500 hover:text-white transition hidden sm:block">Rides</a>
            <a href="/" className="text-xs text-zinc-500 hover:text-white transition hidden sm:block">HealthAI</a>
            {locationName && <span className="text-xs text-zinc-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{locationName}</span>}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl sm:text-4xl font-bold mb-2 bg-gradient-to-r from-yellow-400 via-amber-400 to-orange-400 bg-clip-text text-transparent">
            Compare grocery prices
          </h2>
          <p className="text-zinc-500 text-sm mb-6">See which app is cheaper, then order in one tap</p>
        </div>

        {/* Search */}
        <div className="max-w-xl mx-auto mb-8 flex gap-2" data-testid="search-form">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && search()}
              placeholder="Search a product e.g. Amul Milk"
              className="pl-10 h-12 bg-zinc-900/80 border-zinc-800 text-white rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              data-testid="product-input"
            />
          </div>
          <Button
            onClick={() => search()}
            disabled={loading || !query.trim()}
            className="h-12 px-5 rounded-xl bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-yellow-600 hover:to-amber-700 text-black font-semibold"
            data-testid="compare-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Zap className="w-4 h-4 mr-1" />Compare</>}
          </Button>
        </div>

        {/* Trending */}
        {!results && !loading && (
          <div className="mb-10" data-testid="trending-section">
            <p className="text-[10px] text-zinc-600 uppercase tracking-widest mb-3">Popular searches</p>
            <div className="flex flex-wrap gap-2">
              {TRENDING.map((t) => (
                <button
                  key={t}
                  onClick={() => { setQuery(t); search(t); }}
                  className="px-3 py-1.5 rounded-full bg-zinc-900 border border-zinc-800 text-xs text-zinc-400 hover:text-white hover:border-zinc-600 transition"
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        )}

        {loading && (
          <div className="text-center py-16">
            <div className="inline-flex items-center gap-3 bg-zinc-900 border border-zinc-800 rounded-2xl px-6 py-4">
              <Loader2 className="w-6 h-6 animate-spin text-amber-400" />
              <div className="text-left">
                <p className="font-medium text-sm">Comparing prices...</p>
                <p className="text-xs text-zinc-500">Checking Blinkit & Zepto market rates</p>
              </div>
            </div>
          </div>
        )}

        {/* Results */}
        {results && !loading && (
          <div className="space-y-5" data-testid="results-section">
            {/* Best Deal */}
            {bd && (
              <Card className="bg-gradient-to-r from-green-950/40 to-emerald-950/40 border-green-800/20" data-testid="best-deal">
                <CardContent className="p-5 flex items-center gap-5">
                  <div className="w-12 h-12 rounded-2xl bg-green-500/20 flex items-center justify-center shrink-0">
                    <TrendingDown className="w-6 h-6 text-green-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-green-400 font-medium mb-0.5">Best Deal</p>
                    <h3 className="text-base font-bold truncate">{bd.product_name}</h3>
                    <p className="text-xs text-zinc-400">{bd.pack_size} | {bd.brand}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="flex items-center gap-3">
                      <div><p className="text-[10px] text-yellow-400">Blinkit</p><p className="text-lg font-bold">&#8377;{bd.blinkit.price}</p></div>
                      <span className="text-zinc-600">vs</span>
                      <div><p className="text-[10px] text-purple-400">Zepto</p><p className="text-lg font-bold">&#8377;{bd.zepto.price}</p></div>
                    </div>
                    {bd.savings_pct > 0 && <Badge className="mt-1 bg-green-500/20 text-green-400 text-[10px]">Save {bd.savings_pct}% on {bd.cheaper_on}</Badge>}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Disclaimer */}
            <p className="text-[11px] text-zinc-600 text-center italic">{results.disclaimer}</p>

            {/* Product cards */}
            <div className="space-y-3">
              {results.products?.map((p, i) => (
                <ProductCard key={`prod-${i}`} product={p} />
              ))}
            </div>

            {results.products?.length === 0 && (
              <div className="text-center py-12 text-zinc-500">
                <p className="text-sm">No price estimates available.</p>
                <p className="text-xs mt-1">Try a more common product name.</p>
              </div>
            )}
          </div>
        )}
      </main>

      <footer className="border-t border-zinc-800/50 mt-12">
        <div className="max-w-5xl mx-auto px-4 py-4 text-center text-[11px] text-zinc-700">
          AI market price estimates. Tap "Order" to see live price on Blinkit/Zepto. Both deliver in ~10 min.
        </div>
      </footer>
    </div>
  );
};

const ProductCard = ({ product }) => {
  const b = product.blinkit;
  const z = product.zepto;
  const cheaper = product.cheaper_on;

  return (
    <Card className={`overflow-hidden ${product.savings_pct > 5 ? 'border-green-800/30 bg-green-950/10' : 'bg-zinc-900/50 border-zinc-800/50'}`} data-testid="product-card">
      <CardContent className="p-0">
        {/* Header */}
        <div className="px-4 pt-4 pb-3 border-b border-zinc-800/50">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h4 className="font-bold text-base text-white truncate">{product.product_name}</h4>
              <p className="text-xs text-zinc-500 mt-0.5">
                {product.pack_size}
                {product.brand && product.brand !== product.product_name ? ` | ${product.brand}` : ''}
                {product.category ? ` | ${product.category}` : ''}
              </p>
            </div>
            {product.savings_pct > 0 && (
              <Badge className="bg-green-500/20 text-green-400 text-xs shrink-0">Save {product.savings_pct}%</Badge>
            )}
          </div>
        </div>

        {/* Price comparison */}
        <div className="grid grid-cols-[1fr,auto,1fr] items-stretch">
          {/* Blinkit */}
          <PlatformCol data={b} platform="blinkit" cheaper={cheaper} />

          {/* VS */}
          <div className="flex flex-col items-center justify-center px-3 bg-zinc-800/20">
            <span className="text-xs text-zinc-500 font-bold">VS</span>
            {product.price_diff > 0 && <span className="text-[10px] text-zinc-500 mt-1">&#8377;{product.price_diff}</span>}
          </div>

          {/* Zepto */}
          <PlatformCol data={z} platform="zepto" cheaper={cheaper} />
        </div>
      </CardContent>
    </Card>
  );
};

const PlatformCol = ({ data, platform, cheaper }) => {
  const isCheaper = cheaper === platform;
  const label = platform === 'blinkit' ? 'BLINKIT' : 'ZEPTO';
  const accent = platform === 'blinkit' ? 'text-yellow-400' : 'text-purple-400';
  const btnColor = platform === 'blinkit'
    ? 'bg-yellow-500 hover:bg-yellow-600 text-black'
    : 'bg-purple-600 hover:bg-purple-700 text-white';

  return (
    <div className={`p-4 flex flex-col ${isCheaper ? 'bg-green-950/20' : ''}`}>
      <div className="flex items-center gap-1.5 mb-2">
        <span className={`text-xs font-bold ${accent}`}>{label}</span>
        <span className="text-[9px] text-zinc-500 bg-zinc-800 px-1.5 py-0.5 rounded">est.</span>
      </div>
      <div className="flex items-baseline gap-2">
        <p className="text-2xl font-bold text-white">&#8377;{data.price}</p>
        {data.mrp && data.mrp > data.price && (
          <p className="text-xs text-zinc-500 line-through">&#8377;{data.mrp}</p>
        )}
      </div>
      {data.discount_pct && (
        <p className="text-[10px] text-green-400 flex items-center gap-0.5 mt-0.5">
          <Tag className="w-2.5 h-2.5" />{data.discount_pct}% off
        </p>
      )}
      {data.delivery_time && (
        <p className="text-[11px] text-zinc-600 flex items-center gap-1 mt-1">
          <Clock className="w-3 h-3" />{data.delivery_time}
        </p>
      )}
      {isCheaper && <p className="text-xs text-green-400 font-semibold mt-2">CHEAPER</p>}
      <a
        href={data.order_url}
        target="_blank"
        rel="noopener noreferrer"
        className={`mt-3 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition ${btnColor}`}
        data-testid={`order-on-${platform}-btn`}
      >
        Order on {platform === 'blinkit' ? 'Blinkit' : 'Zepto'} <ExternalLink className="w-3 h-3" />
      </a>
    </div>
  );
};

export default GroceryPriceCompare;
